// English — secondary locale.
export default {
  translation: {
    appName: 'PetPro Booking',
    loading: 'Loading…',
    back: 'Back',
    next: 'Next',
    confirm: 'Confirm booking',
    retry: 'Retry',

    notFound: {
      title: 'Booking page not found',
      body: 'This booking link may be disabled or does not exist.',
    },

    liff: {
      connecting: 'Connecting to LINE…',
      error: "Couldn't connect to LINE. Reopen from LINE, or book with your phone number.",
    },

    steps: {
      service: 'Service',
      dates: 'Dates & room',
      details: 'Pet details',
      verify: 'Verify phone',
      done: 'Done',
    },

    service: {
      title: 'Choose a service',
      boarding: 'Boarding',
      daycare: 'Daycare',
      grooming: 'Grooming',
      boardingDesc: 'Overnight stays',
      daycareDesc: 'Daytime care, same-day pickup',
      groomingDesc: 'Bath, groom, trim',
    },

    grooming: {
      title: 'Choose service & time',
      chooseService: 'Choose a service',
      duration: '{{count}} min',
      chooseGroomer: 'Choose a groomer',
      anyGroomer: 'Any available',
      anyGroomerHint: 'First free groomer',
      date: 'Date',
      findTimes: 'Find available times',
      noSlots: 'No times available on this date. Try another day.',
      pickTime: 'Pick a time',
      time: 'Time',
      from: 'from',
    },

    addon: {
      title: 'Add grooming to the stay?',
      subtitle: 'Optionally book a grooming session during this booking.',
      add: 'Add grooming',
      skip: 'No thanks',
      tag: 'Grooming add-on',
      total: 'Add-ons subtotal',
    },

    dates: {
      title: 'Choose dates',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      search: 'Search availability',
      noRooms: 'No rooms available for these dates. Try other dates.',
      perNight: '/night',
      room: 'Room',
      capacity: 'Fits {{count}}',
      selectRoom: 'Select room',
    },

    details: {
      title: 'Pet & contact details',
      petName: "Pet's name",
      species: 'Species',
      breed: 'Breed (optional)',
      notes: 'Notes (optional)',
      ownerName: 'Your name',
      phone: 'Mobile number',
      phoneHint: "We'll send a verification code to this number",
      specialInstructions: 'Special care needs (optional)',
      dog: 'Dog',
      cat: 'Cat',
      other: 'Other',
    },

    verify: {
      title: 'Verify your phone',
      sent: 'Code sent to {{phone}}',
      codeLabel: 'Enter the 6-digit code',
      sendCode: 'Send code',
      resend: 'Resend',
      verifying: 'Verifying…',
      devCode: 'Dev code: {{code}}',
    },

    review: {
      title: 'Review your booking',
      service: 'Service',
      dates: 'Dates',
      pet: 'Pet',
      contact: 'Contact',
      submitting: 'Submitting…',
    },

    done: {
      requestedTitle: 'Booking request received',
      requestedBody: "The business will confirm shortly. Status: awaiting confirmation.",
      confirmedTitle: 'Booking confirmed!',
      confirmedBody: 'We look forward to caring for your pet.',
      reference: 'Reference',
      bookAnother: 'Book another',
    },

    errors: {
      generic: 'Something went wrong. Please try again.',
      rateLimited: 'Too many attempts. Please try again later.',
      clinicFull: 'This shop is currently at capacity and cannot take new online bookings right now. Please contact the shop directly.',
      badCode: 'Incorrect or expired code.',
      tokenExpired: 'Your session expired. Please verify your phone again.',
      tokenExpiredLiff: 'Your session expired. Please submit again.',
      required: 'This field is required.',
    },

    packages: {
      // Catalog card
      buy: 'Buy',
      sessions: '{{n}} sessions',
      bonusSuffix: ' (incl. {{n}} free)',
      validity: 'Valid {{n}} days',
      transferHousehold: 'Household — any pet in your household',
      transferOwnerOnly: 'One pet only',

      // Checkout sheet
      sheetTitle: 'Buy {{name}}',
      sheetSummary: '{{n}} sessions · {{price}}',
      refundNone: '⚠️ Non-refundable — no refunds after purchase',
      refundProrated: 'Refund policy: prorated by sessions used (fees deducted)',
      refundProratedWithFee:
        'Refund policy: prorated — an early-termination fee applies',
      refundFullWithinWindow:
        'Refund policy: full refund within a window (if unused)',
      einvoiceNotice:
        'The invoice is issued centrally by the Camus platform — please do not ask the shop to issue a separate one.',
      petLabel: 'Eligible pet *',
      petHint:
        'This package is for one pet only and cannot be changed after purchase.',
      petNeedVerify: 'Verify your phone first to choose the eligible pet.',
      petAddMore: 'Add a pet',
      petAddFirst: 'Add my pet',
      petNamePlaceholder: "Pet's name",
      einvoiceCarrierLabel: 'Carrier barcode (e.g. /AB12345)',
      einvoiceTaxIdLabel: 'Or tax ID (8 digits)',
      consent:
        "I have read and agree to the refund policy and this package's terms.",
      cancel: 'Cancel',
      paying: 'Processing…',
      pay: 'Pay {{price}}',
      errNeedVerify: 'Verify your phone number before buying a package.',
      errNeedConsent: 'Please accept the terms.',
      errNeedEinvoice: 'Please provide a carrier barcode or tax ID.',
      errNeedPet: 'This package is for one pet — please choose the eligible pet.',
      errNeedPetName: "Please enter the pet's name.",
      errCheckoutFailed: 'Checkout failed. Please try again.',

      // Post-payment return
      returnConfirmingTitle: 'Confirming payment…',
      returnConfirmingBody:
        'Confirming your package with the payment provider…',
      returnSuccessTitle: '✅ Purchase complete',
      returnSuccessBody: 'Added “{{name}}” — {{n}} sessions.',
      returnSuccessBodyExpiry:
        'Added “{{name}}” — {{n}} sessions, valid until {{date}}.',
      returnSuccessGeneric:
        'Payment complete — your package is active and ready to use when booking.',
      returnPendingTitle: 'Payment processing',
      returnPendingBody:
        'We’ve received your payment; activation can take a few seconds. Check “My packages” shortly.',
      returnCancelTitle: 'Checkout cancelled',
      returnCancelBody: 'You didn’t complete payment; no package was purchased.',
      close: 'Close',

      // Header CTA + catalog
      headerCta: '🎁 Packages ({{n}})',
      catalogTitle: 'Prepaid packages',

      // Redeem picker
      redeemTitle: 'Use a prepaid package?',
      redeemSubtitle: 'This booking will use {{n}} session(s)',
      redeemNone: "Don't use a package (pay later)",
      redeemOption: '{{name}} · {{remaining}}/{{total}} left',
      redeemInsufficientTag: ' (not enough)',
      redeemInsufficientHint:
        "Your packages don't have enough sessions left for this booking.",

      // Add-on picker
      addonTitle: 'Add-ons (optional)',
      decrease: 'Decrease',
      increase: 'Increase',
    },
  },
}
